import { CreateMemberReminderDtoToneEnum } from 'src/Services/API/generated';

export interface MemberReminderTemplateInput {
  memberName: string;
  amount: number;
  gestionnaireName: string;
  assetName: string;
  contactInfo?: string;
}

export interface MemberReminderTemplateOutput {
  subject: string;
  message: string;
}

/**
 * Client-side preview only, mirrors src/member-reminders/member-reminder-templates.ts on the
 * API — kept in sync manually. The gestionnaire can edit the result 100% before sending, and
 * the backend never regenerates it; this is purely for the live preview in the modal.
 */
const formatAmount = (amount: number): string => `${Math.abs(amount).toFixed(2)} €`;

const buildAmicalReminder = (input: MemberReminderTemplateInput): MemberReminderTemplateOutput => ({
  subject: `Petit rappel pour "${input.assetName}"`,
  message: [
    `Bonjour ${input.memberName},`,
    '',
    `J'espère que tout va bien ! Je me permets un petit rappel amical : il reste un solde de ${formatAmount(input.amount)} à régler concernant "${input.assetName}".`,
    '',
    `N'hésite pas à me contacter si besoin${input.contactInfo ? ` (${input.contactInfo})` : ''}.`,
    '',
    'Merci et à bientôt,',
    input.gestionnaireName,
  ].join('\n'),
});

const buildNeutreReminder = (input: MemberReminderTemplateInput): MemberReminderTemplateOutput => ({
  subject: `Rappel de solde - ${input.assetName}`,
  message: [
    `Bonjour ${input.memberName},`,
    '',
    `Ceci est un rappel concernant votre solde sur "${input.assetName}" : un montant de ${formatAmount(input.amount)} reste à régler.`,
    '',
    'Merci de bien vouloir procéder au règlement dans les meilleurs délais.',
    '',
    'Cordialement,',
    input.gestionnaireName,
  ].join('\n'),
});

const buildFermeReminder = (input: MemberReminderTemplateInput): MemberReminderTemplateOutput => ({
  subject: `Rappel important - solde impayé sur "${input.assetName}"`,
  message: [
    `Bonjour ${input.memberName},`,
    '',
    `Malgré nos précédents échanges, un solde de ${formatAmount(input.amount)} reste impayé concernant "${input.assetName}".`,
    '',
    `Merci de régulariser cette situation dans les plus brefs délais${input.contactInfo ? ` (contact : ${input.contactInfo})` : ''}.`,
    '',
    'Cordialement,',
    input.gestionnaireName,
  ].join('\n'),
});

export const buildReminderTemplate = (
  tone: CreateMemberReminderDtoToneEnum,
  input: MemberReminderTemplateInput
): MemberReminderTemplateOutput => {
  switch (tone) {
    case CreateMemberReminderDtoToneEnum.Amical:
      return buildAmicalReminder(input);
    case CreateMemberReminderDtoToneEnum.Neutre:
      return buildNeutreReminder(input);
    case CreateMemberReminderDtoToneEnum.Ferme:
      return buildFermeReminder(input);
  }
};
